// src/scenes/licenses/components/LicenseEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../../components/Header";
import { getLicenseById, updateLicense } from "../../../services/licenseService";
import { getVehiclesForCurrentUser } from "../../../services/vehicleService";
import { tokens } from "../../../theme";

const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const LicenseEdit = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const cardBg = isDark ? "#1e2a40" : "#fdfcfc";
  const cardBorder = isDark ? "#2b3b55" : "#d1d5db";
  const inputBg = isDark ? colors.primary[500] : "#ececec";
  const inputText = isDark ? colors.grey[100] : "#000000";
  const labelColor = isDark ? "#ffffff" : "#000000";
  const accent = colors.blueAccent[400];
  const navigate = useNavigate();
  const { id } = useParams();
  const { userId, driverId, type: userType } = useSelector((state) => state.user);

  const [licenseNumber, setLicenseNumber] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const inputSx = {
    flexGrow: 1,
    "& .MuiInputLabel-root": { color: labelColor },
    "& .MuiInputLabel-root.Mui-focused": { color: inputText },
    "& .MuiInputBase-input": { color: inputText },
    "& .MuiInputBase-input.Mui-focused": { color: inputText },
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputBg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: cardBorder,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: cardBorder,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: 2,
      },
    },
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const lic = await getLicenseById(id);
        setLicenseNumber(lic.licenseNumber || "");
        setVehicleId(lic.vehicle?._id || "");
      } catch (err) {
        setToast({ type: "error", msg: err?.message || "No se pudo cargar la licencia." });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    const loadVehicles = async () => {
      if (!userId && !driverId) return;
      try {
        const data = await getVehiclesForCurrentUser({ userId, driverId, userType });
        setVehicles(data || []);
      } catch (err) {
        setVehicles([]);
      }
    };
    loadVehicles();
  }, [driverId, userId, userType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!licenseNumber.trim()) return setToast({ type: "error", msg: "El número de licencia es obligatorio." });
    try {
      setLoading(true);
      await updateLicense(id, { licenseNumber: licenseNumber.trim(), vehicle: vehicleId || null });
      setToast({ type: "success", msg: "Licencia actualizada correctamente." });
      navigate("/licenses");
    } catch (err) {
      setToast({ type: "error", msg: err?.message || "No se pudo actualizar la licencia." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ m: "20px" }}>
        <Header title="Licencias" subtitle="Editar licencia" />
      </Box>
      <Box
        sx={{
          minHeight: "calc(100vh - 220px)",
          display: "grid",
          placeItems: "start center",
          px: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: "14px",
            p: 3,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 8px 22px rgba(2,6,23,0.08)"
                : "0 3px 10px rgba(0,0,0,0.24)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: "900px",
            mx: "auto",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={1.25}
          >
            <Box>
              <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
                Editar licencia
              </Typography>
              <Typography variant="body2" sx={{ color: palette.label }}>
                Actualiza los datos de la licencia seleccionada.
              </Typography>
            </Box>

            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: cardBorder,
                  color: inputText,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: cardBorder },
                }}
              >
                Volver
              </Button>
              <Button
                type="submit"
                form="license-form"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: palette.btn,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { backgroundColor: palette.btnHover },
                }}
              >
                {loading ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Stack>
          </Stack>

          <Box
            component="form"
            id="license-form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: "transparent",
              border: `1px dashed ${cardBorder}`,
              borderRadius: "12px",
              p: 3,
              width: "100%",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Número de licencia"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  required
                  helperText="Ej. ABC-123 o el identificador oficial"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
                <TextField
                  select
                  fullWidth
                  label="Vehículo (opcional)"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  helperText={
                    vehicles.length
                      ? "Asocia un vehículo a la licencia (opcional)."
                      : "No hay vehículos disponibles todavía."
                  }
                  disabled={vehicles.length === 0}
                  sx={inputSx}
                >
                  {vehicles.map((v) => (
                    <MenuItem key={v._id} value={v._id}>
                      {[v.brand, v.model].filter(Boolean).join(" ") || "Vehículo"}
                      {v.licensePlate ? ` (${v.licensePlate})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Snackbar
          open={!!toast}
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {toast ? (
            <Alert onClose={() => setToast(null)} severity={toast.type} variant="filled" sx={{ width: "100%" }}>
              {toast.msg}
            </Alert>
          ) : null}
        </Snackbar>
      </Box>
    </>
  );
};

export default LicenseEdit;
